import { useEffect, useState, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import { COMPANY } from "@/data/site";

const links = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services", hasDropdown: true },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const SERVICE_LINKS = [
  { to: "/services/web-development", label: "Web Development", desc: "React, Next.js, Node platforms" },
  { to: "/services/mobile-apps",     label: "Mobile Apps",      desc: "iOS, Android, React Native" },
  { to: "/services/hosting",         label: "Hosting Solutions", desc: "Shared, VPS, Cloud, Managed" },
  { to: "/services/blockchain",      label: "Blockchain & Web3", desc: "Smart contracts, DeFi, NFTs" },
  { to: "/services/seo",             label: "SEO & Growth",     desc: "Rank higher, convert better" },
  { to: "/services",                 label: "All Services →",   desc: "View the full menu" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [drop, setDrop] = useState(false);
  const loc = useLocation();
  const dropTimer = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setDrop(false); }, [loc.pathname]);

  const openDrop = () => { clearTimeout(dropTimer.current); setDrop(true); };
  const closeDrop = () => { dropTimer.current = setTimeout(() => setDrop(false), 120); };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-[#09090B]/80 border-b border-[#27272A]" : "bg-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-[72px]">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <img src="/images/logo.png" alt="Jeet IT" className="w-10 h-10 object-contain" />
          <div className="leading-tight">
            <div className="font-display font-bold text-white tracking-tight text-[15px] uppercase">Jeet IT</div>
            <div className="label-mono text-[9px] tracking-[0.3em]">Services / EST. 2016</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            l.hasDropdown ? (
              <div
                key={l.to}
                className="relative"
                onMouseEnter={openDrop}
                onMouseLeave={closeDrop}
              >
                <NavLink
                  to={l.to}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `px-4 py-2 text-sm font-medium tracking-wide flex items-center gap-1 transition-colors ${
                      isActive || drop ? "text-[#DC1F33]" : "text-zinc-300 hover:text-white"
                    }`
                  }
                >
                  {l.label} <ChevronDown size={14} className={`transition-transform ${drop ? "rotate-180" : ""}`} />
                </NavLink>
                {drop && (
                  <div
                    data-testid="services-dropdown"
                    className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[480px] bg-[#0E0E10] border border-[#27272A] shadow-2xl"
                    onMouseEnter={openDrop}
                    onMouseLeave={closeDrop}
                  >
                    <div className="grid grid-cols-2 gap-px bg-[#1f1f22] border border-[#1f1f22]">
                      {SERVICE_LINKS.map((s) => (
                        <Link
                          key={s.to}
                          to={s.to}
                          data-testid={`dropdown-${s.label.toLowerCase().replace(/\W+/g, "-")}`}
                          className="bg-[#0E0E10] hover:bg-[#131316] p-4 group transition-colors"
                        >
                          <div className="font-display font-bold text-white text-sm tracking-tight group-hover:text-[#DC1F33] transition-colors">{s.label}</div>
                          <div className="label-mono mt-1 text-[10px]">{s.desc}</div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm font-medium tracking-wide transition-colors ${
                    isActive ? "text-[#DC1F33]" : "text-zinc-300 hover:text-white"
                  }`
                }
              >
                {l.label}
              </NavLink>
            )
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link to="/contact" data-testid="nav-cta-button" className="hidden lg:inline-flex btn-primary !py-3 !px-5">
            Free Consult <ArrowUpRight size={16} strokeWidth={2.5} />
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-10 h-10 border border-[#27272A] flex items-center justify-center text-white"
            aria-label="Toggle menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-[#27272A] bg-[#0B0B0D] max-h-[calc(100vh-72px)] overflow-y-auto">
          <div className="container-x py-6 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `py-3 border-b border-[#1f1f22] font-display uppercase tracking-wide ${
                    isActive ? "text-[#DC1F33]" : "text-zinc-200"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="label-mono mt-5 mb-2">Services</div>
            {SERVICE_LINKS.slice(0, 5).map((s) => (
              <Link key={s.to} to={s.to} data-testid={`mobile-svc-${s.label.toLowerCase().replace(/\W+/g, "-")}`} className="py-2 text-zinc-300 hover:text-[#DC1F33] text-sm">
                {s.label}
              </Link>
            ))}
            <Link to="/contact" data-testid="mobile-cta" className="btn-primary mt-5">
              Free Consult <ArrowUpRight size={16} />
            </Link>
            <a href={COMPANY.whatsapp} target="_blank" rel="noreferrer" data-testid="mobile-whatsapp" className="btn-ghost mt-2">
              Chat on WhatsApp
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
